import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import { lt } from 'date-fns/locale';
import { COACHES, getMailQueueEntries } from '../utils/firebaseUtils';

const getEffectiveMailStatus = (mail) => {
  const delivery = mail.delivery || {};
  const deliveryInfo = delivery.info || {};
  const accepted = Array.isArray(deliveryInfo.accepted) ? deliveryInfo.accepted : [];
  const rejected = Array.isArray(deliveryInfo.rejected) ? deliveryInfo.rejected : [];
  const pending = Array.isArray(deliveryInfo.pending) ? deliveryInfo.pending : [];
  const response = typeof deliveryInfo.response === 'string' ? deliveryInfo.response : '';
  const hasDeliveryError = Boolean(delivery.error || delivery.state === 'ERROR' || delivery.state === 'RETRY');
  const hasSentSignal =
    accepted.length > 0 ||
    response.toLowerCase().includes('status=202') ||
    Boolean(deliveryInfo.messageId) ||
    Boolean(deliveryInfo.sendgridQueueId);

  if (hasDeliveryError || rejected.length > 0) return 'failed';
  if (hasSentSignal) return 'sent';
  if (pending.length > 0 || delivery.state === 'PENDING' || delivery.state === 'PROCESSING') return 'pending';

  // Fallback to legacy status if extension metadata is missing.
  return mail.status || 'pending';
};

const AdminMailsPage = () => {
  const [mails, setMails] = useState([]);
  const [search, setSearch] = useState('');
  const [showClients, setShowClients] = useState(true);
  const [showCoaches, setShowCoaches] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadMails = async () => {
      try {
        const data = await getMailQueueEntries();
        if (mounted) setMails(data);
      } catch (e) {
        console.error('Failed to load mail queue:', e);
        if (mounted) setError('Nepavyko užkrauti laiškų eilės.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadMails();
    return () => {
      mounted = false;
    };
  }, []);

  const normalizedSearch = search.trim().toLowerCase();
  const coachEmails = useMemo(
    () =>
      new Set(
        COACHES.map((coach) => (coach.email || '').trim().toLowerCase()).filter(Boolean)
      ),
    []
  );

  const getRecipientType = useCallback(
    (mail) => {
      const recipientsRaw = Array.isArray(mail.to) ? mail.to : [mail.to];
      const recipients = recipientsRaw
        .map((recipient) => String(recipient || '').trim().toLowerCase())
        .filter(Boolean);

      const hasCoachRecipient = recipients.some((recipient) => coachEmails.has(recipient));
      return hasCoachRecipient ? 'coach' : 'client';
    },
    [coachEmails]
  );

  const filteredMails = useMemo(() => {
    return mails.filter((mail) => {
      const recipientType = getRecipientType(mail);
      if (!showCoaches && recipientType === 'coach') return false;
      if (!showClients && recipientType === 'client') return false;

      const clientName = (mail.clientName || '').toLowerCase();
      const locationName = (mail.locationName || '').toLowerCase();
      const locationId = (mail.locationId || '').toLowerCase();
      if (!normalizedSearch) return true;
      return (
        clientName.includes(normalizedSearch) ||
        locationName.includes(normalizedSearch) ||
        locationId.includes(normalizedSearch)
      );
    });
  }, [mails, normalizedSearch, showClients, showCoaches, getRecipientType]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Mails
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visi laiškai iš Firebase `mail` eilės (naujausi viršuje).
      </Typography>

      <TextField
        fullWidth
        label="Ieškoti pagal klientą arba vietą"
        placeholder="Pvz.: Jonas arba SEB Arena"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2, maxWidth: 520 }}
      />
      <FormGroup row sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={showClients} onChange={(event) => setShowClients(event.target.checked)} />}
          label="Clients"
        />
        <FormControlLabel
          control={<Checkbox checked={showCoaches} onChange={(event) => setShowCoaches(event.target.checked)} />}
          label="Coaches"
        />
      </FormGroup>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Sukurta</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Klientas</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Vieta</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Kam</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Tema</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Įrašų nerasta.
                </TableCell>
              </TableRow>
            ) : (
              filteredMails.map((mail) => (
                <TableRow key={mail.id} hover>
                  <TableCell>
                    {mail.createdAt ? format(mail.createdAt, 'yyyy-MM-dd HH:mm', { locale: lt }) : '—'}
                  </TableCell>
                  <TableCell>{mail.clientName || '—'}</TableCell>
                  <TableCell>{mail.locationName || mail.locationId || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
                    {Array.isArray(mail.to) ? mail.to.join(', ') : (mail.to || '—')}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                    {mail.message?.subject || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getRecipientType(mail) === 'coach' ? 'coach' : 'client'}
                      variant="outlined"
                      sx={{ mr: 0.75, textTransform: 'lowercase' }}
                    />
                    {(() => {
                      const effectiveStatus = getEffectiveMailStatus(mail);
                      return (
                        <Chip
                          size="small"
                          label={effectiveStatus}
                          color={
                            effectiveStatus === 'sent'
                              ? 'success'
                              : effectiveStatus === 'failed'
                                ? 'error'
                                : 'warning'
                          }
                          variant="outlined"
                        />
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminMailsPage;
